import { Box, Flex, SystemProps, Text } from "@storyofams/react-ui";
import Imgix from "react-imgix";
import styled from "styled-components";

import { convertCSStoObject, replaceVariables } from "~/context/helper";
import { FileUnion } from "~/graphql/api/sdk";
import { Flow } from "~/context";
import { useFlow, useSession } from "~/hooks";
import { getFileUrl } from "~/lib";
import { OptionProps } from "../Options/Option";

interface OptionImageProps extends Pick<OptionProps, "imageType" | "type"> {
  src?: FileUnion;
  title: string;
  subtext?: string;
  pageSettings?: any;
}

const ImgWrapper = styled.div<Pick<OptionImageProps, "imageType" | "pageSettings"> & { flow: Flow }>`
  width: 100%;
  height: ${(p) => (p.imageType === "white-bg" ? "210px" : `${p.pageSettings?.imageHeight ?? "180"}px`)};
  padding: 10px;
  margin: ${(p) => (p.imageType === "white-bg" ? `${p.theme.space[3]}px ${p.theme.space[3]}px 0` : 0)};
  ${(p) => convertCSStoObject(p?.flow?.cssEditor)[".answer-image-container"]}
  display: flex;
  justify-content: center;
  align-items: center;

  > img {
    width: auto;
    height: 100%;
    object-fit: ${(p) => p.pageSettings?.imageMode || "cover"};
    object-position: center;
    aspect-ratio: ${(p) =>
      p.pageSettings?.imageShape === "circle" || p.pageSettings?.imageShape === "square"
        ? "1 / 1"
        : p.pageSettings?.imageShape === "portrait"
        ? "9 / 16"
        : "16 / 9"};
    border-radius: ${(p) => (p.pageSettings?.imageShape === "circle" ? "100%" : "5px")};
    ${(p) => ({ ...convertCSStoObject(p?.flow?.cssEditor)[".answer-image"], ...convertCSStoObject(p?.flow?.cssEditor)[".image"] })}
  }
`;

export const OptionImage = ({ src, title, imageType, subtext, pageSettings }: OptionImageProps & SystemProps) => {
  const { flow } = useFlow();
  const { session } = useSession();
  const sessionVariables = session?.answers ? JSON.parse(session?.answers) : {};

  return (
    <Flex flexDirection="column" className="answer-image-text-container" width="100%">
      <ImgWrapper className="answer-image-container" flow={flow} pageSettings={pageSettings}>
        {!!src && (
          <>
            {process.env.NODE_ENV === "development" && (!src || !("url" in src)) ? (
              <img src={getFileUrl(src)} alt={title ?? "answer image"} className="answer-image" />
            ) : (
              <Imgix
                className="answer-image"
                src={getFileUrl(src)}
                sizes="735px"
                disableSrcSet={true} // Forces Imgix to output a src attribute
                attributeConfig={{
                  src: "src", // Ensure src is always set
                  srcSet: "srcSet", // Allow srcset but ensure src exists
                  sizes: "sizes",
                }}
                htmlAttributes={{
                  alt: title ?? "answer image",
                  loading: "lazy",
                }}
              />
            )}
          </>
        )}
      </ImgWrapper>

      <Box p={3} pt={imageType === "white-bg" ? 2 : 3} textAlign="start" width="100%">
        <Text
          fontSize={2}
          lineHeight="19px"
          color={imageType === "white-bg" ? "black90" : "black80"}
          className="answer-text-with-image"
          textAlign="center"
          style={convertCSStoObject(flow?.cssEditor)[".answer-text-with-image"]}
        >
          <div dangerouslySetInnerHTML={{ __html: replaceVariables(title, sessionVariables) ?? title }} />
        </Text>
        {!!subtext && (
          <Text mt={0.25} fontSize={1.5} lineHeight="15px" color="black60">
            {subtext}
          </Text>
        )}
      </Box>
    </Flex>
  );
};
